import { useMemo } from "react";
import PageBanner from "@/components/feature/PageBanner";
import AboutTabs from "./components/AboutTabs";
import { useApi } from "@/hooks/useApi";
import type { ClubInfo, HistoryItem } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";

interface HistoryGroup {
  year: string;
  items: HistoryItem[];
}

export default function History() {
  const { data, loading, error, reload } = useApi<ClubInfo>("club.php");
  const history = data?.history ?? [];

  const groups = useMemo<HistoryGroup[]>(() => {
    const map = new Map<string, HistoryItem[]>();
    history.forEach((h) => {
      const year = h.year || "기타";
      if (!map.has(year)) map.set(year, []);
      map.get(year)!.push(h);
    });
    return Array.from(map.entries()).map(([year, items]) => ({ year, items }));
  }, [history]);

  return (
    <>
      <PageBanner
        title="연혁"
        description="대전민들레로타리클럽이 걸어온 봉사의 발자취를 소개합니다."
        breadcrumb={[{ label: "클럽소개", to: "/about/greeting" }, { label: "연혁" }]}
      />
      <AboutTabs />

      <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
        {loading ? (
          <div className="space-y-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-6 w-24" />
                <div className="space-y-3 pl-6">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="연혁을 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                다시 시도
              </button>
            }
          />
        ) : groups.length === 0 ? (
          <EmptyState icon="ri-history-line" title="등록된 연혁이 없습니다" />
        ) : (
          <ol className="relative space-y-10">
            {groups.map((group, gi) => (
              <li key={group.year} className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-primary-500 text-background-50 font-heading font-bold text-sm whitespace-nowrap">
                    {group.year}
                  </span>
                  <span className="h-px flex-1 bg-background-200" aria-hidden="true"></span>
                </div>

                <div
                  className={`relative space-y-6 ${
                    gi < groups.length ? "" : ""
                  } pl-6 md:pl-8`}
                >
                  {/* 세로 라인 */}
                  <span
                    className="absolute left-[5px] top-2 bottom-2 w-px bg-background-300"
                    aria-hidden="true"
                  ></span>
                  {group.items.map((item, i) => (
                    <div key={`${group.year}-${i}`} className="relative">
                      <span
                        className="absolute -left-[23px] top-1.5 w-3 h-3 rounded-full border-2 border-background-50 bg-secondary-500 md:-left-[31px]"
                        aria-hidden="true"
                      ></span>
                      {item.date && (
                        <span className="text-xs font-semibold text-secondary-800">
                          {item.date}
                        </span>
                      )}
                      <p className="mt-0.5 text-sm md:text-base leading-relaxed text-foreground-700">
                        {item.content}
                      </p>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );
}